/**
 * Issue #57 — traduire les échecs d'`apiClient` en un message que l'utilisateur peut utiliser.
 *
 * Le défaut que ce module ferme n'est pas une erreur de code : c'est que **l'erreur technique était
 * le message d'interface**. Chaque page affichait `err.message` dans sa propre bannière, donc ce
 * que l'utilisateur lisait dépendait de la couche qui avait échoué — et parfois du navigateur :
 * « NetworkError when attempting to fetch resource » est le libellé de Firefox, Chrome écrit
 * « Failed to fetch », Safari « Load failed ». Trois phrases, un seul incident, aucune en français.
 * La règle `stack-frontend-design.md` demande une interface **100 % en français** ; elle n'était
 * pas tenue là où elle compte le plus, c'est-à-dire quand plus rien ne marche.
 *
 * ## Ce que ce module fait, et ce qu'il refuse de faire
 *
 * Il classe l'échec par **famille** et rend un message par famille. Il ne fabrique **jamais** un
 * message à partir du texte technique : il le range dans `detail`, à afficher sous un « Détail
 * technique » repliable, comme le fait déjà `AuthErrorScreen`. Rien n'est perdu, tout est déplacé.
 *
 * Le point délicat est la **frontière entre message métier et message technique**. Quand l'API
 * refuse une action pour une raison qu'elle sait nommer — « l'audit récurrent n'est possible que
 * pour un client en offre récurrent » — la remplacer par un message générique serait une
 * régression : l'API en dit plus que nous. On lui fait donc confiance **en dessous de 500**, où le
 * corps de réponse est un refus métier écrit pour un humain, et jamais **à partir de 500**, où le
 * message est un accident (trace, code interne, texte d'un pilote SQL) qui n'a rien à faire à
 * l'écran.
 */

/**
 * Le code que pose `apiClient` quand `fetch` rejette — c'est-à-dire quand la requête n'a jamais
 * atteint le serveur. Aucun statut HTTP n'existe alors : `status` vaut 0, par convention.
 */
export const CODE_RESEAU = 'NETWORK'

export type FamilleErreur =
  | 'reseau'
  | 'session'
  | 'droits'
  | 'introuvable'
  | 'refus'
  | 'saturation'
  | 'serveur'
  | 'inconnue'

export interface MessageErreur {
  /** La famille retenue — utile aux tests et à la télémétrie, jamais affichée telle quelle. */
  famille: FamilleErreur
  /** Ce que l'utilisateur lit. Toujours en français, toujours non vide. */
  texte: string
  /** Ce qu'il peut faire, quand il peut faire quelque chose. Libellé de bouton. */
  action?: string
  /** Vrai quand se reconnecter est la seule issue — distinct d'un refus de droits. */
  sessionPerdue: boolean
  /** Le libellé d'origine, conservé pour le diagnostic. Jamais affiché sans repli. */
  detail: string
}

/**
 * Les libellés de coupure réseau des trois moteurs, plus les formulations génériques.
 *
 * `fetch` ne rejette que par `TypeError`, mais un `TypeError` n'est pas forcément une coupure
 * réseau — une faute de programmation en produit un aussi. On reconnaît donc sur le **libellé**,
 * pas sur le type : un libellé inconnu retombe sur le message générique, qui est français lui
 * aussi. Se tromper de famille coûterait plus cher que ne pas la reconnaître.
 */
const LIBELLES_RESEAU =
  /networkerror|failed to fetch|load failed|network request failed|réseau indisponible|err_internet/i

/**
 * Les deux formes que prend une session perdue dans ce dépôt.
 *
 * `apiClient` traduit tout échec d'acquisition de jeton en `ApiError(401)`, mais l'inverse n'est
 * pas vrai : `getToken` peut échouer **avant** toute requête et remonter une `Error` nue selon le
 * chemin. Reconnaître les deux évite qu'une porte de sortie dépende du chemin emprunté.
 */
const LIBELLES_SESSION =
  /jeton d[’']acc[eè]s|session expir[ée]e|no active account|interaction_required|login_required/i

/**
 * La forme d'une `ApiError`, reconnue **structurellement**.
 *
 * Ce module est appelé PAR `apiClient` : un `import { ApiError }` en sens inverse fermerait un
 * cycle d'imports, dont l'ordre d'évaluation dépendrait ensuite du bundler. La forme suffit —
 * `status` numérique et `code` textuel sur une `Error` —, et elle a l'avantage de couvrir aussi
 * une erreur reconstruite (désérialisation, frontière de worker).
 */
interface FormeApiError extends Error {
  status: number
  code: string
}

function estApiError(e: unknown): e is FormeApiError {
  if (!(e instanceof Error)) return false
  const candidat = e as Partial<FormeApiError>
  return typeof candidat.status === 'number' && typeof candidat.code === 'string'
}

function texteBrut(e: unknown): string {
  if (e instanceof Error) return e.message
  if (typeof e === 'string') return e
  return ''
}

function detailDe(e: unknown): string {
  if (estApiError(e)) {
    // `ApiError` pose `message = code` quand l'API n'a rien écrit : ne pas le répéter.
    const complement = e.message && e.message !== e.code ? ` — ${e.message}` : ''
    return `HTTP ${e.status} · ${e.code}${complement}`
  }
  if (e instanceof Error) return e.message || e.name
  if (typeof e === 'string') return e
  return String(e)
}

/**
 * Vrai quand la seule issue est de se reconnecter.
 *
 * Exporté séparément parce que c'est la question que se pose la **racine** de l'application, qui
 * n'a pas besoin du message : elle a besoin de savoir s'il faut proposer la porte de sortie.
 */
export function EST_SESSION_PERDUE(e: unknown): boolean {
  if (estApiError(e)) return e.status === 401
  return LIBELLES_SESSION.test(texteBrut(e))
}

function familleDeStatut(status: number): FamilleErreur {
  if (status === 403) return 'droits'
  if (status === 404) return 'introuvable'
  if (status === 408 || status === 429) return 'saturation'
  if (status >= 500) return 'serveur'
  if (status >= 400) return 'refus'
  return 'inconnue'
}

/**
 * Le texte de la famille d'un statut, exporté pour `apiClient`.
 *
 * C'est ce qui rend la correction **exhaustive sans réécrire 161 appelants** : plutôt que de
 * migrer chaque `err.message` affiché dans une page, `apiClient` remplit lui-même le message quand
 * l'API n'en fournit aucun. Le code technique (`UNKNOWN`, `INTERNAL_ERROR`) cesse d'être ce que
 * l'utilisateur lit, et reste ce que le diagnostic trouve dans `code`.
 */
export function texteParDefaut(status: number): string {
  return texteDeStatut(status)
}

export const TEXTE_RESEAU =
  'La connexion au portail a échoué. Vérifiez votre accès au réseau, puis relancez l’action.'

function texteDeStatut(status: number): string {
  switch (familleDeStatut(status)) {
    case 'droits':
      return "Vous n'avez pas les autorisations nécessaires pour cette action."
    case 'introuvable':
      return "L'élément demandé n'existe pas ou n'est plus disponible."
    case 'saturation':
      return 'Le portail est momentanément saturé. Réessayez dans quelques instants.'
    case 'serveur':
      return 'Le portail a rencontré un incident. Réessayez dans quelques instants ; si le problème persiste, contactez le support.'
    case 'refus':
      return "La demande a été refusée : certaines informations sont invalides ou incompatibles avec l'état actuel."
    default:
      return TEXTE_GENERIQUE
  }
}

const TEXTE_GENERIQUE =
  "Une erreur inattendue s'est produite. Réessayez ; si le problème persiste, contactez le support."

export function messageUtilisateur(e: unknown): MessageErreur {
  const detail = detailDe(e)

  if (EST_SESSION_PERDUE(e)) {
    return {
      famille: 'session',
      texte: 'Votre session a expiré. Reconnectez-vous pour continuer.',
      action: 'Se reconnecter',
      sessionPerdue: true,
      detail,
    }
  }

  if (LIBELLES_RESEAU.test(texteBrut(e))) {
    return {
      famille: 'reseau',
      texte: TEXTE_RESEAU,
      action: 'Réessayer',
      sessionPerdue: false,
      detail,
    }
  }

  if (estApiError(e)) {
    const famille = e.code === CODE_RESEAU ? 'reseau' : familleDeStatut(e.status)
    // Le message de l'API prime en dessous de 500 : c'est un refus métier, écrit pour un humain et
    // plus précis que tout ce qu'on saurait dire d'ici. À partir de 500, il est technique.
    const metier = e.status < 500 && e.message && e.message !== e.code ? e.message : null
    return {
      famille,
      texte: metier ?? texteDeStatut(e.status),
      action: famille === 'droits' ? undefined : 'Réessayer',
      sessionPerdue: false,
      detail,
    }
  }

  return { famille: 'inconnue', texte: TEXTE_GENERIQUE, action: 'Réessayer', sessionPerdue: false, detail }
}
